// Enhanced style system with modern design
export const S = {
  page: {
    maxWidth: 1000,
    margin: "24px auto",
    padding: 20,
    fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
  },
  enrolmentPage: {
    margin: "24px auto",
    padding: 20,
    fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
  },
  card: {
    background: "#fff",
    border: "1px solid #e5e7eb",
    borderRadius: 16,
    padding: 32,
    boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
    backdropFilter: "blur(10px)"
  },
  row: {
    display: "grid",
    gridTemplateColumns: "repeat(12, 1fr)",
    gap: 16,
    marginBottom: 16,
    alignItems: "end"
  },
  col: (span = 12) => ({
    gridColumn: `span ${span} / span ${span}`
  }),
  h1: {
    fontSize: 32,
    fontWeight: 700,
    margin: "0 0 8px",
    color: "#111827",
    background: "linear-gradient(135deg, #056ec4 0%, #192b64 100%)",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
    backgroundClip: "text"
  },
  h2: {
    fontSize: 20,
    fontWeight: 600,
    margin: "24px 0 16px",
    color: "#374151",
    display: "flex",
    alignItems: "center",
    gap: 8
  },
  label: {
    fontWeight: 600,
    fontSize: 14,
    color: "#374151",
    marginBottom: 6,
    display: "block"
  },
  input: {
    width: "100%",
    padding: "12px 16px",
    border: "2px solid #e5e7eb",
    borderRadius: 10,
    fontSize: 14,
    transition: "all 0.2s ease",
    boxSizing: "border-box",
    background: "#fafafa",
    "&:focus": {
      outline: "none",
      borderColor: "#667eea",
      background: "#fff",
      boxShadow: "0 0 0 3px rgba(102, 126, 234, 0.1)"
    }
  },
  inputFocus: {
    outline: "none",
    borderColor: "#667eea",
    background: "#fff",
    boxShadow: "0 0 0 3px rgba(102, 126, 234, 0.1)"
  },
  inputError: {
    borderColor: "#ef4444",
    boxShadow: "0 0 0 3px rgba(239, 68, 68, 0.1)"
  },
  select: {
    width: "100%",
    padding: "12px 16px",
    border: "2px solid #e5e7eb",
    borderRadius: 10,
    fontSize: 14,
    background: "#fafafa",
    cursor: "pointer",
    transition: "all 0.2s ease",
    boxSizing: "border-box"
  },
  textarea: {
    width: "100%",
    padding: "12px 16px",
    border: "2px solid #e5e7eb",
    borderRadius: 10,
    fontSize: 14,
    minHeight: 100,
    resize: "vertical",
    background: "#fafafa",
    transition: "all 0.2s ease",
    fontFamily: "inherit",
    boxSizing: "border-box"
  },
  hint: {
    fontSize: 12,
    color: "#6b7280",
    marginTop: 4,
    fontStyle: "italic"
  },
  error: {
    fontSize: 12,
    color: "#ef4444",
    marginTop: 4,
    display: "flex",
    alignItems: "center",
    gap: 4
  },
  button: (variant = "primary", size = "md") => ({
    padding: size === "sm" ? "4px 8px" : "12px 24px",
    borderRadius: 10,
    border: "2px solid transparent",
    cursor: "pointer",
    fontSize: size === "sm" ? 11 : 16,
    fontWeight: 600,
    transition: "all 0.2s ease",
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    background: variant === "primary" 
      ? "linear-gradient(135deg, #056ec4 0%, #192b64 100%)"
      : variant === "secondary"
      ? "#f3f4f6"
      : "transparent",
    color: variant === "primary" 
      ? "#fff" 
      : variant === "secondary"
      ? "#374151"
      : "#6b7280",
    borderColor: variant === "outline" ? "#d1d5db" : "transparent",
    "&:hover": {
      transform: "translateY(-1px)",
      boxShadow: variant === "primary" 
        ? "0 8px 25px rgba(102, 126, 234, 0.3)"
        : "0 4px 12px rgba(0,0,0,0.1)"
    },
    "&:disabled": {
      opacity: 0.6,
      cursor: "not-allowed",
      transform: "none"
    }
  }),
  stepper: {
    display: "flex",
    flexWrap: "wrap",
    gap: 8,
    marginBottom: 24,
    padding: "16px 0"
  },
  pill: (active, completed = false) => ({
    padding: "10px 16px",
    borderRadius: 25,
    border: "2px solid",
    fontSize: 13,
    fontWeight: 600,
    cursor: "pointer",
    transition: "all 0.3s ease",
    display: "flex",
    alignItems: "center",
    gap: 6,
    background: active 
      ? "linear-gradient(135deg, #056ec4 0%, #192b64 100%)"
      : completed
      ? "#10b981"
      : "#fff",
    color: active || completed ? "#fff" : "#6b7280",
    borderColor: active 
      ? "transparent"
      : completed
      ? "#10b981"
      : "#d1d5db",
    boxShadow: active 
      ? "0 4px 15px rgba(102, 126, 234, 0.3)"
      : completed
      ? "0 4px 15px rgba(16, 185, 129, 0.3)"
      : "0 2px 8px rgba(0,0,0,0.06)",
    "&:hover": {
      transform: "translateY(-2px)",
      boxShadow: active 
        ? "0 6px 20px rgba(102, 126, 234, 0.4)"
        : completed
        ? "0 6px 20px rgba(16, 185, 129, 0.4)"
        : "0 4px 15px rgba(0,0,0,0.1)"
    }
  }),
  fileUpload: {
    border: "2px dashed #d1d5db",
    borderRadius: 10,
    padding: 20,
    textAlign: "center",
    cursor: "pointer",
    transition: "all 0.2s ease",
    background: "#fafafa",
    "&:hover": {
      borderColor: "#667eea",
      background: "#f8faff"
    }
  },
  checkbox: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: "8px 0"
  },
  checkboxGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
    gap: 8,
    marginTop: 8
  },
  sectionCard: {
    border: "1px solid #e5e7eb",
    borderRadius: 12,
    padding: 20,
    marginBottom: 16,
    background: "#fafafa"
  },
  navigation: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginTop: 32,
    paddingTop: 24,
    borderTop: "1px solid #e5e7eb"
  },
  navButton: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: "12px 24px",
    borderRadius: 10,
    border: "2px solid transparent",
    cursor: "pointer",
    fontSize: 16,
    fontWeight: 600,
    transition: "all 0.2s ease"
  },
  successCard: {
    marginTop: 16,
    padding: 20,
    border: "2px solid #10b981",
    borderRadius: 12,
    background: "linear-gradient(135deg, #ecfdf5 0%, #f0fdf4 100%)",
    display: "flex",
    alignItems: "top",
    gap: 12
  },
  errorCard: {
    marginTop: 16,
    padding: 20,
    border: "2px solid #ef4444",
    borderRadius: 12,
    background: "linear-gradient(135deg, #fef2f2 0%, #fef7f7 100%)",
    display: "flex",
    alignItems: "center",
    gap: 12
  }
};